<script setup lang="ts">
    import { useI18n } from 'vue-i18n';
    import { useMarketQuery } from '@/composables/market';
    import { computed } from 'vue';
    import { isMobile } from '@/util/breakpoints';
    import { useRouter, useRoute } from 'vue-router';
    import { Navigation } from '@/router/navigation';
    import { useContainexPathHelper } from '@/util/containex-path-helper.ts';
    import { useCompanyQuery } from '@/composables/company.ts';
    import { MarketType } from '@containex/portal-backend-dto';

    const { t } = useI18n();
    const { market } = useMarketQuery();
    const router = useRouter();
    const route = useRoute();
    const containexPathHelper = useContainexPathHelper();
    const { isRetailer: isRetailerCompany } = useCompanyQuery();

    const isMyArea = computed(() => {
        return route.path.includes('/account');
    });

    const isRetailerMarket = computed(() => market?.value?.type === MarketType.Retailer);
    const shouldApplyMobileCartViewPadding = computed(() => {
        const routeName = router.currentRoute.value.name?.toString();
        if (isMobile.value && routeName != null) {
            return [Navigation.SalesCart, Navigation.RentalCart].some((n) => n === routeName);
        }
        return false;
    });
</script>

<template>
    <div
        class="footer-container"
        :class="{
            'footer-container__mobile': isMobile,
            'footer-container__mobile-cart-view': shouldApplyMobileCartViewPadding,
        }"
    >
        <footer class="footer">
            <div v-if="isRetailerMarket && !isMyArea" class="retailer-market-disclaimer">
                <span class="pi pi-info-circle"></span>
                <div>
                    {{ t('NAVIGATION.FOOTER_INFO') }}
                    <template v-if="isRetailerCompany">
                        {{ t('NAVIGATION.FOOTER_RETAILER_MARKET_DISCLAIMER_RETAILER_COMPANY') }}
                    </template>
                    <template v-else>
                        {{ t('NAVIGATION.FOOTER_RETAILER_MARKET_DISCLAIMER') }}
                    </template>
                </div>
            </div>

            <ul class="info-container container">
                <li v-if="!isRetailerMarket && !isMyArea" class="info-container-item">
                    <span class="pi pi-info-circle"></span>
                    {{ t('NAVIGATION.FOOTER_INFO') }}
                </li>
                <li class="info-container-item">
                    <span class="pi pi-external-link"></span>
                    <a :href="containexPathHelper.retrievePrivacyPolicyUrl()" class="text-link link" target="_blank">{{
                        t('NAVIGATION.PRIVACY_POLICY')
                    }}</a>
                </li>
                <li class="info-container-item">
                    <span class="pi pi-external-link"></span>
                    <a :href="containexPathHelper.retrieveTermsOfUseUrl()" class="text-link link" target="_blank">{{
                        t('NAVIGATION.TERMS_OF_USE')
                    }}</a>
                </li>
                <li class="info-container-item">
                    <span class="pi pi-external-link"></span>
                    <a :href="containexPathHelper.retrieveImprintUrl()" class="text-link link" target="_blank">{{
                        t('NAVIGATION.IMPRINT')
                    }}</a>
                </li>
                <li class="info-container-item">
                    <span class="pi pi-external-link"></span>
                    <a
                        href="#"
                        class="text-link link optanon-toggle-display onetrust-pc-btn-handler"
                        data-tracking-intern="footer-cookiesettings"
                    >
                        {{ t('NAVIGATION.COOKIE_SETTINGS') }}
                    </a>
                </li>
            </ul>
        </footer>
    </div>
</template>

<style scoped lang="scss">
    @use '@/styling/main';

    .footer-container {
        @include main.apply-scrollbar-spacing;

        position: sticky;
        top: 100vh;
        background-color: main.$color-background-bluegray;

        border-top: 1px solid main.$color-surface-200;

        &__mobile {
            padding-bottom: main.$spacing-height-nav-bar-footer;
        }

        &__mobile-cart-view {
            padding-bottom: calc(main.$spacing-height-nav-bar-footer + main.$mobile-cart-price-footer-height);
        }
    }

    .footer {
        max-width: 1200px;
        padding: main.$spacing-6;

        margin: 0 auto;
    }

    .retailer-market-disclaimer {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 24px;
    }

    .container {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        align-items: flex-start;

        > :deep(*) {
            flex: none;
            width: 100%;
            padding: main.$spacing-2;
        }
    }

    @include main.for-breakpoint-md {
        .container > :deep(*) {
            flex: none;
            width: auto;
            padding: 0;
        }
    }

    :deep(.footer-menu) {
        border: none;
        background-color: main.$color-background-bluegray;
    }

    :deep(.footer-menu .p-submenu-header) {
        background-color: main.$color-background-bluegray;
    }

    .info-container {
        list-style: none;
        margin: 0;
        padding: 0;
    }

    .link {
        color: main.$color-secondary-blue;
    }

    .info-container-item {
        display: flex;
        gap: main.$spacing-3;
        align-items: center;
    }
</style>
